<template>
  <el-card shadow="never" v-loading="loading">
    <div class="header" slot="header">
      <h4>Complete Lease</h4>
      <div v-if="leaseTransfer && leaseTransfer.stages" class="stages-overview">
        <h6 class="overview-title">Lease Transfer Stages</h6>
        <div class="stages-grid">
          <div
            v-for="stage in leaseTransfer.stages"
            :key="stage.step"
            :class="['stage-item', { 'active': stage.active, 'completed': isStageCompleted(stage) }]"
          >
            <div class="stage-header">
              <el-tag
                :type="getStageTagType(stage)"
                size="small"
                class="stage-tag"
              >
                <span>
                  Stage {{ stage.step}}
                </span>
              </el-tag>
              <span class="stage-status">{{ stage.status }}</span>
            </div>
            <div class="stage-dates">
              <span class="stage-date">{{ stage.start | formatDate }}</span>
              <span v-if="stage.end" class="stage-date"> - {{ stage.end | formatDate }}</span>
            </div>
            <div v-if="stage.active" class="current-indicator">
              <el-icon name="el-icon-location"></el-icon>
              <span>Currently Completing</span>
            </div>
          </div>
        </div>
      </div>
      <el-steps
        :active="completeLeaseActive"
        finish-status="success"
        align-center
        class="completion-steps"
      >
        <el-step title="Move out type"> </el-step>
        <el-step title="Transfer Funds"> </el-step>
        <el-step title="Done" :status="completeStatus"></el-step>
      </el-steps>
    </div>
    <transition name="el-fade-in-linear">
      <el-card shadow="never" v-show="completeLeaseActive === 0" class="step-card">
        <div class="step-header">
          <h5>Step 1: Transfer Type</h5>
          <p class="step-description">
            <span v-if="currentStageInfo">Completing Stage {{ currentStageInfo.step }}:</span>
            Select how the member is transferring to determine refund calculations.
          </p>
        </div>
        
        <el-form
          :model="completeLeaseForm.moveOut"
          :rules="completeLeaseForm.moveOutRules"
          ref="confirm"
          class="move-out-form"
        >
          <div class="form-section">
            <el-form-item label="Select Transfer type" class="move-out-type-item">
              <el-select
                placeholder="Select type of transfer"
                v-model="completeLeaseForm.moveOut.type"
                size="large"
                @change="clearCompleteLeaseDate"
                class="move-out-select"
              >
                <el-option value="scheduled" label="As Scheduled"></el-option>
                <el-option value="earlier" label="Earlier / Before Scheduled Date" disabled></el-option>
                <el-option value="delayed" label="Delayed / After Scheduled Date" disabled></el-option>
              </el-select>
            </el-form-item>
            
            <el-form-item
              v-if="completeLeaseForm.moveOut.type !== 'scheduled'"
              prop="moveOutAdjustment"
              label="Actual Moved out date"
              class="date-picker-item"
            >
              <el-date-picker
                v-model="completeLeaseForm.moveOut.moveOutAdjustment"
                :picker-options="completeLeaseDatePickerOptions"
                placeholder="Actual Move Out Date"
                @change="moveOutAdjustmentChange"
                class="move-out-date-picker"
              >
              </el-date-picker>
            </el-form-item>
          </div>
          
          <div v-if="conflicts.length" class="conflicts-section">
            <el-alert
              title="Schedule Conflicts Detected"
              type="warning"
              :closable="false"
              show-icon
              class="conflicts-alert"
            >
              <p>The selected move-out date conflicts with existing leases. Please review the conflicts below:</p>
            </el-alert>
            
            <el-table :data="conflicts" class="conflicts-table">
              <el-table-column prop="unit.name" label="Unit" width="120"></el-table-column>
              <el-table-column
                prop="moveIn"
                label="Conflict Move In"
                :formatter="(row, column, cellValue) => $options.filters.formatDate(cellValue, this.$root.state.facilityData)"
                width="150"
              ></el-table-column>
              <el-table-column prop="customer.company" label="Customer"></el-table-column>
            </el-table>
          </div>
          
          <div class="step-actions">
            <el-button 
              type="primary" 
              @click="next"
              :disabled="conflicts.length > 0"
              class="next-button"
            >
              Next Step
            </el-button>
          </div>
        </el-form>
      </el-card>
    </transition>
    <transition name="el-fade-in-linear">
      <el-card shadow="never" v-show="completeLeaseActive === 1" class="step-card">
        <div class="step-header">
          <h5>Step 2: Transfer Funds</h5>
          <p class="step-description">
            <span v-if="currentStageInfo">For Stage {{ currentStageInfo.step }}:</span>
            Review and configure refund calculations based on the transfer type.
          </p>
        </div>
        
        <el-form
          :model="completeLeaseForm.refund"
          :rules="completeLeaseForm.refundRules"
          ref="refund"
          class="refund-form"
        >
          <!-- Overview Section -->
          <div class="overview-section">
            <h6 class="section-title">Financial Overview</h6>
            <el-table :data="computeLeaseSecurityDepositToBeRefundedBasedOnDue()" class="overview-table">
              <el-table-column prop="descriptions" label="Item"></el-table-column>
              <el-table-column prop="amount" label="Amount" align="right" :formatter="(row, column, cellValue) => $options.filters.currencySign(cellValue, this.$root.state.facilityData)"></el-table-column>
            </el-table>
          </div>
          
          <!-- Move-out Type Specific Configuration -->
          <div class="configuration-section">
            <h6 class="section-title">Move-out Configuration</h6>
            
            <!-- Earlier Move-out -->
            <div v-if="completeLeaseForm.moveOut.type === 'earlier'" class="move-out-config">
              <el-alert
                title="Early Move-out Refund"
                type="info"
                :closable="false"
                show-icon
                class="config-alert"
              >
                <p>Customer is moving out earlier than scheduled. Configure refund for unused days.</p>
              </el-alert>
              
              <div class="config-form">
                <el-form-item label="Enable early move out refund" class="checkbox-item">
                  <el-checkbox 
                    @change="() => completeLeaseForm.refund.numRefundDays = 0" 
                    v-model="completeLeaseForm.refund.enabled"
                    class="refund-checkbox"
                  >
                    Enable refund calculation
                  </el-checkbox>
                </el-form-item>
                
                <div v-if="completeLeaseForm.refund.enabled" class="refund-details">
                  <el-form-item label="Number of days to refund" prop="numRefundDays" class="days-input-item">
                    <el-input-number 
                      :disabled="!completeLeaseForm.refund.enabled" 
                      v-model="completeLeaseForm.refund.numRefundDays" 
                      :min="0"
                      :max="30"
                      class="days-input"
                    ></el-input-number>
                    <span class="input-help">Maximum 30 days</span>
                  </el-form-item>
                  
                  <div class="refund-amount-display">
                    <div class="amount-item">
                      <span class="amount-label">Refund Amount:</span>
                      <span class="amount-value refund-amount">{{ refundedAmountForEarlyMoveout | currencySign($root.state.facilityData) }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Scheduled Move-out -->
            <div v-if="completeLeaseForm.moveOut.type === 'scheduled'" class="move-out-config">
              <el-alert
                title="Scheduled Move-out"
                type="success"
                :closable="false"
                show-icon
                class="config-alert"
              >
                <p>Customer is moving out as scheduled. No additional fees or refunds apply.</p>
              </el-alert>
            </div>
            
            <!-- Delayed Move-out -->
            <div v-if="completeLeaseForm.moveOut.type === 'delayed'" class="move-out-config">
              <el-alert
                title="Delayed Move-out"
                type="warning"
                :closable="false"
                show-icon
                class="config-alert"
              >
                <p>Customer is moving out after the scheduled date. Late fees may apply.</p>
              </el-alert>
              
              <div class="config-form">
                <el-form-item label="Charge Late Move-Out Fee" class="checkbox-item">
                  <el-checkbox v-model="enableLateFee" class="late-fee-checkbox">
                    Apply late move-out fee
                  </el-checkbox>
                </el-form-item>
                
                <div v-if="enableLateFee" class="late-fee-details">
                  <el-form-item label="Late Move-Out Fee Amount" class="fee-input-item">
                    <el-input-number 
                      :disabled="!enableLateFee" 
                      @change="() => rewriteLateMoveOutFee = true" 
                      v-model="newLateMoveOutFee" 
                      :min="0" 
                      :step="0.5"
                      class="fee-input"
                    ></el-input-number>
                  </el-form-item>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Total Amount Summary -->
          <div class="total-summary-section">
            <h6 class="section-title">Transfer Summary</h6>
            <div class="total-amount-card">
              <div class="total-amount-header">
                <span class="total-label">Total Amount to Transfer to Next Stage</span>
                <span class="total-value">{{ overpaymentToBeRefunded + depositToBeRefunded | currencySign($root.state.facilityData) }}</span>
              </div>
            </div>
          </div>
          
          <!-- Step Actions -->
          <div class="step-actions">
            <el-button
              type="default"
              @click="() => (completeLeaseActive -= 1)"
              class="back-button"
            >
              Back
            </el-button>
            <el-button 
              type="primary" 
              @click="next"
              class="next-button"
            >
              Next Step
            </el-button>
          </div>
        </el-form>
      </el-card>
    </transition>
    <transition name="el-fade-in-linear">
      <el-card shadow="never" v-show="completeLeaseActive === 2" class="step-card">
        <div class="step-header">
          <h5>Step 3: Review & Complete</h5>
          <p class="step-description">
            <span v-if="currentStageInfo">Finalizing Stage {{ currentStageInfo.step }}:</span>
            Review the final details and complete the lease termination.
          </p>
        </div>
        
        <div class="review-section">
          <h6 class="section-title">Move-out Summary</h6>
          <div class="summary-card">
            <div class="summary-item">
              <span class="summary-label">Move-out Type:</span>
              <el-tag :type="getMoveOutTypeTagType(completeLeaseForm.moveOut.type)" class="summary-tag">
                {{ completeLeaseForm.moveOut.type.toUpperCase() }}
              </el-tag>
            </div>
          </div>
        </div>
        
        <div v-if="moveOutTable().length" class="line-items-section">
          <h6 class="section-title">Line Items</h6>
          <el-table :data="moveOutTable()" class="line-items-table" fit>
            <el-table-column
              prop="amount"
              label="Amount"
              width="120"
              align="right"
              :formatter="(row, column, cellValue) => $options.filters.currencySign(cellValue, this.$root.state.facilityData)"
            ></el-table-column>
            <el-table-column label="Description" prop="description"></el-table-column>
          </el-table>
        </div>
        
        <div class="completion-actions">
          <el-button
            type="default"
            @click="() => (completeLeaseActive -= 1)"
            class="back-button"
          >
            Back
          </el-button>
          <el-button
            type="success"
            @click="completeLease"
            class="complete-button"
            :loading="loading"
          >
            Complete Lease
          </el-button>
        </div>
      </el-card>
    </transition>
    <!-- Success State -->
    <transition name="el-fade-in-linear">
      <el-card shadow="never" v-show="completeLeaseActive === 3 && completeStatus === 'success' && !loading" class="result-card success-card">
        <el-result
          icon="success"
          title="Lease Successfully Completed"
          subTitle="The lease has been terminated and funds have been transferred to the next stage."
        >
          <template slot="extra">
            <el-button type="primary" @click="$emit('completed')">
              Continue
            </el-button>
          </template>
        </el-result>
      </el-card>
    </transition>
    
    <!-- Processing State -->
    <transition name="el-fade-in-linear">
      <el-card shadow="never" v-show="completeLeaseActive === 3 && completeStatus === 'process' && loading" class="result-card processing-card">
        <div class="processing-content">
          <el-progress
            :percentage="100"
            :stroke-width="8"
            status="success"
            :show-text="false"
          />
          <h4>Processing Lease Completion</h4>
          <p>Please wait while we process the lease termination and transfer funds...</p>
        </div>
      </el-card>
    </transition>
    
    <!-- Error State -->
    <transition name="el-fade-in-linear">
      <el-card shadow="never" v-show="completeLeaseActive === 3 && completeStatus === 'error' && !loading" class="result-card error-card">
        <el-result
          icon="error"
          title="Something went wrong"
          subTitle="There was an error completing the lease. Please try again or contact support."
        >
          <template slot="extra">
            <el-button type="primary" @click="() => (completeLeaseActive = 1)">
              Try Again
            </el-button>
            <el-button type="default" @click="$emit('completed')">
              Cancel
            </el-button>
          </template>
        </el-result>
      </el-card>
    </transition>
  </el-card>
</template>

<script>
import axios from 'axios';
import { addMilliseconds, differenceInCalendarDays, lastDayOfMonth } from 'date-fns';
import { setMoveoutToUTC, setMoveinToUTC, allowedCompleteLeaseDate } from '../utils/timing';
import { UNIT_RENTAL_API_BASE, CUSTOMER_API_BASE, LEASING_API, ADMIN_API_BASE } from '../env';

export default {
  name: 'MoveOutUnit',
  props: {
    customerId: { type: String, default: null },
    facility: { type: String, required: true },
    leaseTransfer: {
      type: Object,
      default: null,
    },
  },
  data() {
    const moveOutValidate = (rule, value, callback) => {
      if (value < new Date(this.activeLease.start)) {
        callback(new Error('Move out date can not be less than start date.'));
      } else {
        callback();
      }
    };

    const completeLeaseDateValidator = (date) => {
      const moveInDate = new Date(this.activeLease.start);
      const moveOutDate = new Date(this.activeLease.end);
      return !allowedCompleteLeaseDate(date, moveInDate, moveOutDate, this.completeLeaseForm.moveOut.type)
    };

    return {
      invoices: [],
      conflicts: [],
      facilityData: null,
      unitRentals: [],
      diffDays: 0,
      lineItems: [],
      completeLeaseActive: 0,
      completedStatus: false,
      errorStatus: false,
      loading: false,
      enableLateFee: false,
      rewriteLateMoveOutFee: false,
      newLateMoveOutFee: 0,
      lateFeeFromSecurityDesposit: false,
      completeLeaseForm: {
        moveOut: {
          confirmMoveOut: false,
          moveOutDelayed: false,
          type: 'scheduled',
          moveOutAdjustment: null,
        },
        refund: {
          numRefundDays: 0,
          enabled: false,
        },
        moveOutRules: {
          moveOutAdjustment: {
            validator: moveOutValidate,
            trigger: 'blur',
          },
        },
        refundRules: {
          numRefundDays: [
            { type: 'integer', min: 0, max: 30, message: 'Number of days (max. 30)', trigger: ['blur', 'change'] },
          ],
        },
        managerRating: {
          rating: null,
          feedback: '',
          addedBy: '',
        },
      },
      completeLeaseDatePickerOptions: {
        disabledDate: completeLeaseDateValidator,
      },
    };
  },
  computed: {
    activeLease() {
      if (this.invoices.length) {
        const [activeL] = this.invoices.filter(lease => lease && !lease.future && lease.leaseStarted && lease.end && !lease.completed);
        return activeL;
      }
      return null;
    },
    activeStage() {
      if (!this.leaseTransfer || !this.leaseTransfer.stages) return null;
      return this.leaseTransfer.stages.find(stage => stage.active);
    },
    currentStageInfo() {
      if (!this.leaseTransfer) return null;
      return this.activeStage;
    },
    payableAmount() {
      if (this.activeLease) {
        const amountAfterApplyingAnyRentRefund = this.activeLease.amount
          - this.refundedAmountForEarlyMoveout;
        return amountAfterApplyingAnyRentRefund - this.activeLease.deposit;
      }

      return 0;
    },
    payableAmountPaid() {
      if (this.activeLease) {
        const payableAmountPaid = this.activeLease.amountPaid - this.activeLease.depositPaid;
        return payableAmountPaid;
      }

      return 0;
    },
    dueBalance() {
      const payableDueBalance = this.payableAmount - this.payableAmountPaid;

      if (payableDueBalance < 0) {
        return this.totalDelayedFeePlusTax;
      }
      return payableDueBalance + this.totalDelayedFeePlusTax;
    },
    totalDelayedFee() {
      if (!this.enableLateFee) {
        return 0;
      }

      let delayedFee = 0;
      this.unitRentals.forEach((unit) => {
        delayedFee += this.computeLateMoveOutFee(unit);
      });
      return this.rewriteLateMoveOutFee
        ? this.newLateMoveOutFee : delayedFee;
    },
    totalDelayedFeePlusTax() {
      if (!this.enableLateFee) {
        return 0;
      }

      return this.moveOutTable().map(item => item.amount).reduce((p, n) => p + n, 0);
    },
    lastLineItemAccountBalance() {
      const { lineItems } = this.activeLease;
      // let accountBalance = 0;
      // lineItems.forEach((item) => {
      //   accountBalance += item.amount;
      // });
      return Math.round(lineItems[lineItems.length - 1].accountBalance * 100) / 100;
    },
    completeStatus() {
      if (this.completeLeaseActive < 3) {
        return 'wait';
      }

      if (this.completedStatus) {
        return 'success';
      }

      if (this.errorStatus) {
        return 'error';
      }

      return 'process';
    },
    amountWhichCanBePaidBySecurityDeposit() {
      // overpayment && dueBalance clear with the overpayment
      if (this.overPayment > this.dueBalance) {
        return 0;
      }

      if (this.overPayment > 0) {
        if (this.overPayment < this.dueBalance) {
          const amountLeftAfterUsingOverPayment = this.dueBalance - this.overPayment;
  
          if (amountLeftAfterUsingOverPayment >= this.activeLease.depositPaid) {
            return this.activeLease.depositPaid;
          }
  
          return amountLeftAfterUsingOverPayment;
        }
      }

      if (this.activeLease && this.dueBalance >= this.activeLease.depositPaid) {
        return this.activeLease.depositPaid;
      }

      return this.dueBalance;
    },
    balanceAfterUsingSecurityBalance() {
      if (this.dueBalance) {
        return this.dueBalance;
      }
      return 0;
    },
    overPayment() {
      if (this.payableAmount - this.payableAmountPaid < 0) {
        return this.payableAmountPaid - this.payableAmount;
      }
      return 0;
    },
    overPaymentToClearDuesIfAny() {
      if (this.balanceAfterUsingSecurityBalance && this.overPayment) {
        return this.balanceAfterUsingSecurityBalance > this.overPayment ? this.overPayment
          : this.balanceAfterUsingSecurityBalance;
      }
      return 0;
    },
    overpaymentToBeRefunded() {
      return Math.round((this.overPayment - this.overPaymentToClearDuesIfAny) * 100) / 100;
    },
    depositToBeRefunded() {
      if (this.activeLease) {
        return this.activeLease.depositPaid - this.amountWhichCanBePaidBySecurityDeposit;
      }

      return 0;
    },
    refundedAmountForEarlyMoveout() {
      if (!this.completeLeaseForm.refund.enabled) {
        return 0;
      }

      const lastDayDate = lastDayOfMonth(this.completeLeaseForm.moveOut.moveOutAdjustment);
      const lastDay = lastDayDate.getDate();

      let sum = 0;
      this.unitRentals.forEach((item) => {
        if (item.updatedPrice) {
          sum += item.updatedPrice;
        } else {
          sum += item.pricing.price;
        }
      });

      const price = (sum / lastDay) * this.completeLeaseForm.refund.numRefundDays;
      return Math.round(price * 100) / 100;
    },
    totalRent() {
      return this.unitRentals.map((rental) => {
        if (rental.updatedPrice) {
          return rental.updatedPrice;
        }
        return rental.pricing.price;
      }).reduce((p, n) => p + n, 0);
    },
  },
  async mounted() {
    try {
      this.loading = true;
      const response = await axios.get(
        `${CUSTOMER_API_BASE}/customers/v1/customer/invoices/${this.customerId}`,
        {
          params: {
            facility: this.facility,
          },
          headers: {
            Authorization: `Bearer ${this.$keycloak.token}`,
          },
        },
      );
      this.invoices = response.data;
      this.loading = false;
    } catch (err) {
      this.loading = false;
      if (err.response && err.response.data) {
        this.$message.error({
          title: 'Error',
          message: err.response.data,
        });
        if (err.response.data === 'access_denied') {
          this.$router.push({ name: '404' });
        }
      }
    }
    await this.getFacility();
    await this.getUnitRentalsOfTheLease();
  },
  methods: {
    moveOutTable() {
      // const table = [];
      switch (this.completeLeaseForm.moveOut.type) {
        case 'earlier':
          return this.prepareEarlierRefundLineItem();
        case 'delayed':
          return this.prepareMoveOutLineItems();
        default:
          return [];
      }
    },
    async getFacility() {
      const facility = await axios.get(
        `${ADMIN_API_BASE}/facilities/${this.facility}`,
        {
          headers: {
            Authorization: `Bearer ${this.$keycloak.token}`,
          },
        },
      );
      this.facilityData = facility.data;
    },
    async getUnitRentalsOfTheLease() {
      const unitRentalsResponse = await axios.get(`${UNIT_RENTAL_API_BASE}`, {
        headers: {
          Authorization: `Bearer ${this.$keycloak.token}`,
        },
        params: {
          unitInvoice: this.activeLease._id,
        },
      });
      this.unitRentals = unitRentalsResponse.data.result;
      return unitRentalsResponse.data.result;
    },
    computeLateMoveOutFee(unit) {
      let unitLateFee = 0;
      if (this.completeLeaseForm.moveOut.type === 'delayed') {
        const diffInDays = differenceInCalendarDays(this.completeLeaseForm.moveOut.moveOutAdjustment, new Date(unit.moveOut));
        this.diffDays = diffInDays;
        if (this.diffDays > 0) {
          const price = unit.updatedPrice ? unit.updatedPrice : unit.pricing.price;
          const lastDayDate = lastDayOfMonth(this.completeLeaseForm.moveOut.moveOutAdjustment);
          const lastDay = lastDayDate.getDate();
          const unitLateFeeForOneDay = (price / lastDay);
          const penalty = this.facilityData.penalty / 100;
          unitLateFee = (unitLateFeeForOneDay + (penalty * unitLateFeeForOneDay)) * this.diffDays;
        }
      }
      return Math.round(unitLateFee * 100) / 100;
    },
    computeLeaseSecurityDepositToBeRefundedBasedOnDue() {
      const lineItems = [];
      if (this.activeLease) {
        lineItems.push({
          amount: this.activeLease.depositPaid,
          descriptions: 'Security Deposit Held',
        });

        lineItems.push({
          amount: this.dueBalance,
          descriptions: 'Minus Amount Due',
        });

        lineItems.push({
          amount: this.overPayment,
          descriptions: 'Plus Overpayment',
        });
      }

      // if (this.dueBalance > 0) {
      //   if (this.amountWhichCanBePaidBySecurityDeposit) {
      //     lineItems.push({
      //       amount: this.dueBalance - this.amountWhichCanBePaidBySecurityDeposit,
      //       descriptions: 'Balance after clearing dues using security deposits',
      //     });
      //   }
      // }

      // if (this.completeLeaseForm.moveOut.type === 'delayed') {
      //   lineItems.push({
      //     amount: 'Enabled',
      //     descriptions: 'Late Move-Out Checked',
      //   });

      //   lineItems.push({
      //     amount: this.totalDelayedFee,
      //     descriptions: 'Late Move-Out Fee',
      //   });
      // }

      // if (this.completeLeaseForm.moveOut.type === 'earlier') {
      //   lineItems.push({
      //     amount: 'Enabled',
      //     descriptions: 'Earlier Move-Out Checked',
      //   });

      //   lineItems.push({
      //     amount: this.refundedAmountForEarlyMoveout,
      //     descriptions: 'Refund Rent',
      //   });
      // }

      return lineItems;
    },
    prepareEarlierRefundLineItem() {
      const lineItems = [];
      let accountBalance = this.lastLineItemAccountBalance;
      if (!this.completeLeaseForm.refund.enabled) {
        return [];
      }

      if (this.completeLeaseForm.refund.numRefundDays > 0) {
        this.unitRentals.forEach((unit) => {
          const lastDayDate = lastDayOfMonth(this.completeLeaseForm.moveOut.moveOutAdjustment);
          const lastDay = lastDayDate.getDate();
          const earlyRefundFee = ((unit.updatedPrice ? unit.updatedPrice : unit.pricing.price) / lastDay) * this.completeLeaseForm.refund.numRefundDays;
          accountBalance -= earlyRefundFee;

          const dueDate = setMoveinToUTC(this.completeLeaseForm.moveOut.moveOutAdjustment);
          lineItems.push({
            amount: -(earlyRefundFee),
            description: `Refund applied for Unit ${unit.unit.name} while earlier moveout adjustment for ${this.completeLeaseForm.refund.numRefundDays} days.`,
            chargeType: 'RENT',
            details: 'RENT',
            accountBalance,
            createdAt: new Date(),
            unit: unit.unit._id,
            custom: true,
            period: dueDate,
            dueDate,
            type: 'CREDIT',
            addedBy: this.$keycloak.tokenParsed.email,
          });
        });
      }

      return lineItems;
    },
    prepareMoveOutLineItems() {
      let accountBalance = this.lastLineItemAccountBalance;
      const lineItems = [];
      if (!this.enableLateFee) {
        return [];
      }
      const dueDate = setMoveinToUTC(this.completeLeaseForm.moveOut.moveOutAdjustment);

      if (!this.rewriteLateMoveOutFee) {
        this.unitRentals.forEach((unit) => {
          const unitLateFee = this.computeLateMoveOutFee(unit);
          accountBalance += unitLateFee;
          lineItems.push({
            amount: unitLateFee,
            description: `Rent applied while moveout adjustment for ${this.diffDays} days for unit ${unit.unit.name}`,
            chargeType: 'RENT',
            details: 'RENT',
            accountBalance,
            createdAt: new Date(),
            unit: unit.unit._id,
            custom: true,
            addedBy: this.$keycloak.tokenParsed.email,
            period: dueDate,
            dueDate,
            type: 'CREDIT',
          });
          const unitLateMoveOutTax = Number(this.computeTax(unitLateFee));
          accountBalance += unitLateMoveOutTax;
          if (unitLateMoveOutTax > 0) {
            let description = `Rental Tax for unit ${unit.unit.name} `;
            if (this.facilityData && this.facilityData.tax && this.facilityData.tax.rentTaxLabel) {
              description = `${this.facilityData.tax.rentTaxLabel} @ ${this.facilityData.tax.rentTaxRate}% for unit ${unit.unit.name} rent.`;
            }
            lineItems.push({
              createdAt: new Date(),
              description,
              amount: unitLateMoveOutTax,
              accountBalance,
              unit: unit.unit._id,
              details: 'TAX',
              chargeType: 'TAX',
              custom: true,
              period: dueDate,
              dueDate,
              addedBy: this.$keycloak.tokenParsed.email,
              type: 'CREDIT',
            });
          }
        });
      } else {
        this.unitRentals.forEach((unit) => {
          const unitRentalValue = unit.updatedPrice ? unit.updatedPrice : unit.pricing.price;
          const unitRentalPartInTotalRent = unitRentalValue / this.totalRent;
          const unitLateFee = Math.round(this.newLateMoveOutFee * unitRentalPartInTotalRent * 100) / 100;
          accountBalance += unitLateFee;
          lineItems.push({
            amount: unitLateFee,
            description: `Custom Rent applied while moveout adjustment for ${this.diffDays} days for unit ${unit.unit.name}`,
            chargeType: 'RENT',
            details: 'RENT',
            accountBalance,
            createdAt: new Date(),
            unit: unit.unit._id,
            period: dueDate,
            dueDate,
            custom: true,
            addedBy: this.$keycloak.tokenParsed.email,
            type: 'CREDIT',
          });
          const unitLateMoveOutTax = Number(this.computeTax(unitLateFee));
          accountBalance += unitLateMoveOutTax;
          if (unitLateMoveOutTax > 0) {
            let description = `Rental Tax for unit ${unit.unit.name}`;
            if (this.facilityData && this.facilityData.tax && this.facilityData.tax.rentTaxLabel) {
              description = `${this.facilityData.tax.rentTaxLabel} @ ${this.facilityData.tax.rentTaxRate}% for unit ${unit.unit.name} rent.`;
            }
            lineItems.push({
              createdAt: new Date(),
              description,
              amount: unitLateMoveOutTax,
              accountBalance,
              unit: unit.unit._id,
              addedBy: this.$keycloak.tokenParsed.email,
              period: dueDate,
              dueDate,
              custom: true,
              details: 'TAX',
              chargeType: 'TAX',
              type: 'CREDIT',
            });
          }
        });
      }

      return lineItems;
    },
    async moveOutAdjustmentChange() {
      this.loading = true;
      const response = await axios.get(`${LEASING_API}/v1/${this.facility}/conflicts`, {
        params: {
          lease: this.activeLease._id,
          moveOut: setMoveoutToUTC(this.completeLeaseForm.moveOut.moveOutAdjustment),
        },
        headers: {
          Authorization: `Bearer ${this.$keycloak.token}`,
        },
      });

      this.conflicts = response.data;

      if (this.conflicts.length) {
        this.$alert(`
        <strong>There is a future lease for one or multiple units on this lease that overlaps with the new proposed end date of this lease.
          You must cancel the conflicting future lease(s) prior to changing the end date of this current lease.</strong>
        `, 'Conflict found!', {
          confirmButtonText: 'OK',
          dangerouslyUseHTMLString: true,
        });
      }

      this.loading = false;

      this.rewriteLateMoveOutFee = false;
      this.completeLeaseForm.refund.numRefundDays = 0;

      // when move out date is selected, we call for conflicts.
      // we can set the rewrite flag to false and set the computed late fee.
      if (this.completeLeaseForm.moveOut.type === 'delayed') {
        this.enableLateFee = true;
        this.newLateMoveOutFee = this.totalDelayedFee;
      } else if (this.completeLeaseForm.moveOut.type === 'earlier') {
        const endDate = new Date(this.activeLease.end);
        const adjustmentDate = this.completeLeaseForm.moveOut.moveOutAdjustment;

        const tzEndDate = addMilliseconds(
          new Date(Date.UTC(endDate.getFullYear(), endDate.getMonth(), endDate.getDate())),
          this.$root.state.facilityData.offset * 60 * 100
        );
        const tzAdjustmentDate = addMilliseconds(
          new Date(Date.UTC(adjustmentDate.getFullYear(), adjustmentDate.getMonth(), adjustmentDate.getDate())),
          this.$root.state.facilityData.offset * 60 * 100
        );

        const days = differenceInCalendarDays(tzEndDate, tzAdjustmentDate);
        this.completeLeaseForm.refund.numRefundDays = days;
      }

      return response.data;
    },
    async completeLease() {
      try {
        this.completeLeaseActive += 1;
        this.loading = true;

        await axios.patch(
          `${LEASING_API}/v1/${this.facility}/${this.activeLease._id}/moveout/confirm`,
          {
            depositToBeRefunded: this.depositToBeRefunded,
            overpaymentToBeRefunded: this.overpaymentToBeRefunded,
            moveOutAdjustment: this.completeLeaseForm.moveOut.moveOutAdjustment ? setMoveoutToUTC(this.completeLeaseForm.moveOut.moveOutAdjustment) : null,
            confirmMoveOut: true,
            events: this.activeLease.events,
            units: this.activeLease.units,
            lineItems: this.moveOutTable(),
            amountWhichCanBePaidBySecurityDeposit: this.amountWhichCanBePaidBySecurityDeposit,
            refundRentValue: this.refundedAmountForEarlyMoveout,
            type: this.completeLeaseForm.moveOut.type,
          },
          {
            headers: {
              Authorization: `Bearer ${this.$keycloak.token}`,
            },
          },
        );

        this.completedStatus = true;
        this.$emit('completed');

        // Reset values
        this.completeLeaseForm.moveOut = {
          confirmMoveOut: false,
          moveOutDelayed: false,
          type: 'scheduled',
          moveOutAdjustment: null,
        };

        this.completeLeaseForm.managerRating = {
          rating: null,
          feedback: '',
          addedBy: this.$keycloak.tokenParsed.email,
        };

        this.loading = false;
      } catch (err) {
        console.log(err);
        this.completedStatus = false;
        this.errorStatus = true;
        this.loading = false;
      }
    },
    clearCompleteLeaseDate() {
      this.completeLeaseForm.moveOut.moveOutAdjustment = null;
      this.conflicts = [];
    },
    computeTax(price) {
      if (!this.facilityData.rentTaxFlag) {
        return 0;
      }
      const taxRate = this.facilityData.tax.rentTaxRate;

      const taxValue = price * (taxRate / 100);
      return Math.round(taxValue * 100) / 100;
    },
    getMoveOutTypeTagType(type) {
      switch (type) {
        case 'scheduled':
          return 'success';
        case 'earlier':
          return 'info';
        case 'delayed':
          return 'warning';
        default:
          return 'info';
      }
    },
    isStageCompleted(stage) {
      // A stage is considered completed if it has an end date and is not active
      return stage.end && !stage.active && stage.status === 'completed';
    },
    getStageTagType(stage) {
      if (stage.active) {
        return 'warning';
      }
      if (this.isStageCompleted(stage)) {
        return 'success';
      }
      return 'info';
    },
    next() {
      switch (this.completeLeaseActive) {
        case 0:
          return this.$refs.confirm.validate((valid) => {
            if (valid) {
              if (this.conflicts.length) {
                return;
              }

              if (this.completeLeaseForm.moveOut.type === 'scheduled') {
                const moveout = new Date(this.activeLease.end);
                const currentDate = new Date();
                const days = differenceInCalendarDays(moveout, currentDate);

                if (days < 0) {
                  this.$message.warning({
                    message: 'The scheduled move-out date has already passed. You can now complete the lease.',
                    duration: 5000,
                  });
                } else if (days >= 0) {
                  this.$message.error({
                    message: `You must wait until after the scheduled move-out date (${this.$options.filters.formatDate(moveout, this.$root.state.facilityData)}) to complete the lease.`,
                    duration: 5000,
                  });
                  return;
                }
              }
              this.completeLeaseActive += 1;
            }
          });
        case 1:
          return this.$refs.refund.validate((valid) => {
            if (!valid) { return; }

            this.completeLeaseActive += 1;
          });
        case 2:
          this.completeLeaseActive = 0;
          break;
        default:
          break;
      }
    },
  },
};
</script>

<style scoped>
/* Header and Steps */
.header {
  margin-bottom: 20px;
}

.header h4 {
  margin: 0 0 15px 0;
  color: #303133;
  font-size: 18px;
  font-weight: 600;
}

.stages-overview {
  margin: 15px 0;
  padding: 15px;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e4e7ed;
}

.overview-title {
  margin: 0 0 15px 0;
  color: #303133;
  font-size: 14px;
  font-weight: 600;
}

.stages-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 12px;
}

.stage-item {
  padding: 12px;
  background-color: white;
  border-radius: 6px;
  border: 1px solid #e4e7ed;
  transition: all 0.3s ease;
}

.stage-item.active {
  border-color: #e6a23c;
  background-color: #fdf6ec;
  box-shadow: 0 2px 8px rgba(230, 162, 60, 0.2);
}

.stage-item.completed {
  border-color: #67c23a;
  background-color: #f0f9ff;
}

.stage-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.stage-tag {
  font-weight: 600;
}

.stage-status {
  font-size: 11px;
  color: #606266;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.stage-dates {
  margin-bottom: 8px;
}

.stage-date {
  font-size: 12px;
  color: #909399;
}

.current-indicator {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #e6a23c;
  font-weight: 600;
}

.current-indicator .el-icon {
  font-size: 12px;
}

.completion-steps {
  margin-top: 10px;
}

/* Step Cards */
.step-card {
  margin-bottom: 20px;
  border-radius: 8px;
  border: 1px solid #e4e7ed;
}

.step-header {
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid #f0f0f0;
}

.step-header h5 {
  margin: 0 0 8px 0;
  color: #303133;
  font-size: 16px;
  font-weight: 600;
}

.step-description {
  margin: 0;
  color: #606266;
  font-size: 14px;
  line-height: 1.4;
}

/* Form Sections */
.form-section {
  margin-bottom: 20px;
}

.move-out-type-item,
.date-picker-item {
  margin-bottom: 15px;
}

.move-out-select,
.move-out-date-picker {
  width: 100%;
}

/* Conflicts Section */
.conflicts-section {
  margin-top: 20px;
  padding: 15px;
  background-color: #fef7e6;
  border-radius: 6px;
  border: 1px solid #f5dab1;
}

.conflicts-alert {
  margin-bottom: 15px;
}

.conflicts-alert p {
  margin: 0;
  font-size: 13px;
  line-height: 1.4;
}

.conflicts-table {
  margin-top: 10px;
}

/* Section Titles */
.section-title {
  margin: 0 0 15px 0;
  color: #303133;
  font-size: 14px;
  font-weight: 600;
  padding-bottom: 8px;
  border-bottom: 1px solid #e4e7ed;
}

/* Overview Section */
.overview-section {
  margin-bottom: 25px;
}

.overview-table {
  border-radius: 6px;
  overflow: hidden;
}

/* Configuration Section */
.configuration-section {
  margin-bottom: 25px;
}

.move-out-config {
  margin-bottom: 20px;
}

.config-alert {
  margin-bottom: 15px;
}

.config-alert p {
  margin: 0;
  font-size: 13px;
  line-height: 1.4;
}

.config-form {
  margin-top: 15px;
}

.checkbox-item {
  margin-bottom: 15px;
}

.refund-checkbox,
.late-fee-checkbox {
  font-weight: 500;
}

.refund-details,
.late-fee-details {
  margin-top: 15px;
  padding: 15px;
  background-color: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #e4e7ed;
}

.days-input-item,
.fee-input-item {
  margin-bottom: 15px;
}

.days-input,
.fee-input {
  width: 200px;
}

.input-help {
  margin-left: 10px;
  font-size: 12px;
  color: #909399;
}

.refund-amount-display {
  margin-top: 15px;
}

.amount-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 15px;
  background-color: #f0f9ff;
  border-radius: 6px;
  border: 1px solid #b3d8ff;
}

.amount-label {
  font-weight: 500;
  color: #303133;
}

.amount-value {
  font-weight: 600;
  font-size: 16px;
}

.refund-amount {
  color: #67c23a;
}

/* Total Summary Section */
.total-summary-section {
  margin-bottom: 25px;
}

.total-amount-card {
  padding: 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 8px;
  color: white;
}

.total-amount-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.total-label {
  font-size: 16px;
  font-weight: 600;
}

.total-value {
  font-size: 20px;
  font-weight: 700;
}

/* Review Section */
.review-section {
  margin-bottom: 25px;
}

.summary-card {
  padding: 15px;
  background-color: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #e4e7ed;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.summary-label {
  font-weight: 500;
  color: #303133;
}

.summary-tag {
  font-weight: 600;
}

/* Line Items Section */
.line-items-section {
  margin-bottom: 25px;
}

.line-items-table {
  border-radius: 6px;
  overflow: hidden;
}

/* Step Actions */
.step-actions,
.completion-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 25px;
  padding-top: 20px;
  border-top: 1px solid #f0f0f0;
}

.back-button {
  margin-right: 10px;
}

.next-button,
.complete-button {
  min-width: 120px;
}

/* Result Cards */
.result-card {
  text-align: center;
  padding: 40px 20px;
}

.success-card {
  background-color: #f8f9fa;
  border: 1px solid #e4e7ed;
}

.processing-card {
  background-color: #f8f9fa;
}

.processing-content {
  text-align: center;
}

.processing-content h4 {
  margin: 20px 0 10px 0;
  color: #303133;
  font-size: 18px;
  font-weight: 600;
}

.processing-content p {
  margin: 0;
  color: #606266;
  font-size: 14px;
  line-height: 1.4;
}

.error-card {
  background-color: #fef0f0;
  border: 1px solid #fbc4c4;
}

/* Responsive Design */
@media (max-width: 768px) {
  .stages-grid {
    grid-template-columns: 1fr;
  }
  
  .stage-header {
    flex-direction: column;
    gap: 5px;
    align-items: flex-start;
  }
  
  .step-actions,
  .completion-actions {
    flex-direction: column;
    gap: 10px;
  }
  
  .back-button,
  .next-button,
  .complete-button {
    width: 100%;
  }
  
  .total-amount-header {
    flex-direction: column;
    gap: 10px;
    text-align: center;
  }
  
  .amount-item {
    flex-direction: column;
    gap: 5px;
    text-align: center;
  }
  
  .summary-item {
    flex-direction: column;
    gap: 10px;
    text-align: center;
  }
}

/* Animation Enhancements */
.el-fade-in-linear-enter-active,
.el-fade-in-linear-leave-active {
  transition: all 0.3s ease;
}

.el-fade-in-linear-enter,
.el-fade-in-linear-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

/* Focus States */
.move-out-select:focus,
.move-out-date-picker:focus,
.days-input:focus,
.fee-input:focus {
  border-color: #409eff;
  box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.2);
}

/* Hover Effects */
.step-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: box-shadow 0.3s ease;
}

.next-button:hover,
.complete-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;
}
</style>



// WEBPACK FOOTER //
// src/components/TransferStepComplete.vue